import { useSessionKey } from "@auth/sessionStore";
import {
  Button,
  cn,
  Dialog,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { type ReactNode, useState } from "react";

import type {
  NameFrom,
  RoomOccupant,
  RoomSlot,
  SlotBox,
  SlotStill,
} from "./perceptionClient";
import { useRoomSlot } from "./perceptionQueries";
import { SlotHistory } from "./SlotHistory";
import { StillFrame } from "./StillFrame";
import { ago, clockWord, countWord, visitWord, whoWord } from "./words";

const pct = (fraction: number) => `${fraction * 100}%`;

const when = (iso: string | null, now: number) =>
  iso ? `${clockWord(iso)} (${ago(iso, now) ?? "earlier"})` : "never";

const count = (n: number, one: string, many: string) =>
  countWord(String(n), one, many);

/** Keyed by URL where it is used, so a refreshed still starts unfailed. */
const BoxedStill = ({
  still,
  boxes = [],
  selected,
  onSelect,
}: {
  still: SlotStill;
  boxes?: readonly SlotBox[];
  selected?: string;
  onSelect?: (slot: string) => void;
}) => {
  const [failed, setFailed] = useState(false);
  if (!still.url || failed)
    return (
      <p className="bg-recess p-4 text-sm text-annotation">
        I couldn’t load this still.
      </p>
    );
  return (
    <StillFrame url={still.url} onError={() => setFailed(true)}>
      {boxes.map((box) => {
        const on = box.slot === selected;
        return (
          <button
            key={box.slot}
            type="button"
            aria-label={`Show ${box.slot}`}
            aria-pressed={on}
            onClick={() => onSelect?.(box.slot)}
            className={cn(
              "absolute rounded-sm border-2 focus-visible:outline-2 focus-visible:outline-accent",
              on ? "border-accent" : "border-dashed border-card",
            )}
            style={{
              left: pct(box.x1),
              top: pct(box.y1),
              width: pct(box.x2 - box.x1),
              height: pct(box.y2 - box.y1),
            }}
          >
            <span
              className={cn(
                "absolute top-0 left-0 px-1 font-mono text-fine-print",
                on ? "bg-accent text-ink-inverse" : "bg-card text-ink-body",
              )}
            >
              {box.slot}
            </span>
          </button>
        );
      })}
    </StillFrame>
  );
};

const SlotPicker = ({
  occupants,
  selected,
  onSelect,
}: {
  occupants: readonly RoomOccupant[];
  selected: string;
  onSelect: (slot: string) => void;
}) => (
  <div
    role="group"
    aria-label="Everyone on the roster"
    className="flex flex-wrap gap-1.5"
  >
    {occupants.map((o) => (
      <button
        key={o.slot}
        type="button"
        aria-pressed={o.slot === selected}
        onClick={() => onSelect(o.slot)}
        className={cn(
          "flex items-baseline gap-1.5 rounded-paper border px-2 py-0.5 text-sm transition duration-150 ease-paper focus-visible:outline-2 focus-visible:outline-accent",
          o.slot === selected
            ? "border-accent bg-tint-accent text-ink"
            : "border-rule text-ink-body hover:bg-smudge",
        )}
      >
        <span className="font-mono text-fine-print text-annotation">
          {o.slot}
        </span>
        {whoWord(o)}
      </button>
    ))}
  </div>
);

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className="text-annotation">{label}</dt>
    <dd className="min-w-0 text-ink-body">{children}</dd>
  </>
);

const seenWord = (slot: RoomSlot, now: number) => {
  const { misses, lastSeenAt } = slot.occupant;
  const last = when(lastSeenAt, now);
  return misses === 0
    ? `in view, ${last}`
    : `out of view, missed ${count(misses, "roster check", "roster checks")}; last ${last}`;
};

const HELD: Record<NonNullable<RoomSlot["notHeld"]>, string> = {
  seen_once: "no, seen once",
  anonymous: "no, nothing identifies them yet",
  off: "no, holding is switched off",
};

const nameSources = ({ faces, facesWithoutStill, voice, manual }: NameFrom) => {
  const matches = faces.length + facesWithoutStill;
  return (
    [
      matches > 0 && count(matches, "face match", "face matches"),
      facesWithoutStill > 0 && `${facesWithoutStill} without a still`,
      voice && "their voice",
      manual && "set by hand",
    ]
      .filter(Boolean)
      .join(" · ") || "nothing"
  );
};

const nameWord = (slot: RoomSlot) => {
  const o = slot.occupant;
  if (!o.displayName)
    return slot.nameTries > 0
      ? `none; ${count(slot.nameTries, "face check", "face checks")} gave no name`
      : "none";
  return `${o.displayName} · ${o.nameStatus} · p ${slot.nameP.toFixed(2)}`;
};

const SlotFacts = ({
  slot,
  patients,
  now,
}: {
  slot: RoomSlot;
  patients: ReadonlyMap<string, string>;
  now: number;
}) => {
  const o = slot.occupant;
  const visit = visitWord(o, patients);
  const person = o.kind === "person";
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
      <Fact label="Shown as">
        {whoWord(o)}
        {visit && ` → ${visit}`}
      </Fact>
      <Fact label="Came in">{when(slot.enteredAt, now)}</Fact>
      <Fact label="Seen">{seenWord(slot, now)}</Fact>
      <Fact label="Held unseen">
        {slot.notHeld ? HELD[slot.notHeld] : "yes"}
      </Fact>
      {person && <Fact label="Role">{o.role}</Fact>}
      {person && <Fact label="Head">{slot.headPose}</Fact>}
      {person && <Fact label="Name">{nameWord(slot)}</Fact>}
      {person && o.displayName && (
        <>
          <Fact label="Named from">{nameSources(slot.nameFrom)}</Fact>
          <Fact label="Named at">{when(slot.nameAt, now)}</Fact>
          {slot.supportedAt && (
            <Fact label="Supported at">{when(slot.supportedAt, now)}</Fact>
          )}
        </>
      )}
      <Fact label="Visit">
        {visit ? `${visit} · ${o.visitStatus}` : "none"}
      </Fact>
      {visit && <Fact label="Linked at">{when(slot.visitAt, now)}</Fact>}
      <Fact label="Visit asked">
        {slot.visitAskedAt
          ? `${when(slot.visitAskedAt, now)}, heard ${count(slot.visitHeard, "talk turn", "talk turns")}`
          : "never"}
      </Fact>
      {o.kind === "animal" && (
        <Fact label="In a carrier">{o.carried ? "yes" : "no"}</Fact>
      )}
      {slot.withPerson && <Fact label="Came in with">{slot.withPerson}</Fact>}
    </dl>
  );
};

const SlotBody = ({
  slot,
  patients,
  now,
  onSelect,
}: {
  slot: RoomSlot;
  patients: ReadonlyMap<string, string>;
  now: number;
  onSelect: (slot: string) => void;
}) => {
  const id = slot.occupant.slot;
  const faces = slot.nameFrom.faces;
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start">
      <section
        aria-label={`Where ${id} was last matched`}
        className="flex min-w-0 flex-col gap-1.5 md:w-3/5"
      >
        {slot.seen ? (
          <>
            <BoxedStill
              key={slot.seen.url}
              still={slot.seen}
              boxes={slot.boxes}
              selected={id}
              onSelect={onSelect}
            />
            <p className="font-mono text-fine-print text-annotation">
              The still {id} was last matched on. Boxes are everyone last
              matched on it.
            </p>
          </>
        ) : (
          <EmptyNote className="p-4">
            {slot.occupant.carried
              ? "Counted in a carrier; never boxed."
              : "The camera has not matched this slot yet."}
          </EmptyNote>
        )}
        {faces.length > 0 && (
          <>
            <h3 className="mt-2 text-sm font-semibold text-ink">
              Stills the name was read from
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {faces.map((face) => (
                <li key={face.reference}>
                  <BoxedStill key={face.url} still={face} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      <section
        aria-label={`What ${id} holds`}
        className="flex min-w-0 flex-col gap-4 md:flex-1"
      >
        <SlotFacts slot={slot} patients={patients} now={now} />
        <SlotHistory history={slot.history} patients={patients} />
      </section>
    </div>
  );
};

/**
 * One roster slot in full (BRI-354): its box on the still it was last matched
 * on, the stills its name was read from, what the slot holds and how it got
 * there. Leadership only, since the stills show clients; hidden from RUM, since
 * it names patients. The chips are the roster as it was when the panel opened
 * or was refreshed: a churning roster would move them under the pointer.
 */
export const SlotPanel = ({
  roomId,
  roomName,
  occupants,
  patients,
  slot,
  now,
  onSelect,
  onClose,
}: {
  roomId: string;
  roomName: string;
  occupants: readonly RoomOccupant[];
  patients: ReadonlyMap<string, string>;
  slot: string;
  now: number;
  onSelect: (slot: string) => void;
  onClose: () => void;
}) => {
  const sessionKey = useSessionKey();
  const query = useRoomSlot(sessionKey, roomId, slot);
  const [chips, setChips] = useState(occupants);

  const body = query.isPending ? (
    <SkeletonStack count={2} itemClassName="h-48" />
  ) : query.isError ? (
    <PanelError label={slot} onRetry={() => void query.refetch()} />
  ) : query.data === null ? (
    <EmptyNote className="p-6">{slot} is no longer on the roster.</EmptyNote>
  ) : (
    <SlotBody
      slot={query.data}
      patients={patients}
      now={now}
      onSelect={onSelect}
    />
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${roomName} · ${slot}`}
      footer={
        <>
          {query.dataUpdatedAt > 0 && (
            <span className="mr-auto self-center font-mono text-fine-print text-annotation">
              As of {clockWord(new Date(query.dataUpdatedAt).toISOString())}
            </span>
          )}
          <Button
            variant="ghost"
            disabled={query.isFetching}
            onClick={() => {
              setChips(occupants);
              void query.refetch();
            }}
          >
            Refresh
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div data-dd-privacy="hidden" className="flex flex-col gap-4">
        <SlotPicker occupants={chips} selected={slot} onSelect={onSelect} />
        {body}
      </div>
    </Dialog>
  );
};
